﻿const knownObjProps = {
	"style": 1,
	"dataset": 1,
};

/** @typedef { Record<string, any> & { style?: Record<string, any>, dataset?: Record<string, any>, on?: Record<string, EventListener> } AttrInit */
/** @type { (tagName: string, attributes?: AttrInit|string|Node, ...content: Array<string|Node>) => HTMLElement } */
const createElement = function createElement(tagName/* [, attributes: AttrInit][, ...content: string|Node] */) {
	const element = document.createElement(tagName);
	let i = 1, arg = arguments[i];
	if (arg?.toString() === "[object Object]") {
		Object.entries(arg).forEach(([k, v]) => {
			if (k.startsWith("--")) {
				if (v != null && v !== false)
					element.style.setProperty(k, v);
			} else if (k.startsWith("on") && k.length > 2) {
				element.addEventListener(k.substring(2), v);
			} else if (!(k in knownObjProps) && k !== "on" && v != null && v !== false) {
				// "class" accepts array
				if (k === "class" && Array.isArray(v)) {
					// join non-falsy values
					v = v.filter(c => !!c).join(" ");
					if(v) element.setAttribute(k, v);

				} else {
					element.setAttribute(k, v === true ? "" : v);
				}
			}
		});
		for (const propName in knownObjProps) {
			const argProp = arg[propName];
			if (argProp?.toString() === "[object Object]") {
				const prop = element[propName];
				Object.entries(argProp).forEach(([k, v]) => { prop[k] = v; });
			}
		}
		if (arg.on) {
			for (const event in arg.on) {
				element.addEventListener(event, arg.on[event]);
			}
		}
		i++;
	}
	for (; i < arguments.length; i++) {
		arg = arguments[i];
		if (arg != null && arg !== false)
			element.append(arg);
	}
	return element;
};

function findChild(element, selector) {
	if (element) {
		for (let e = element.firstElementChild; e; e = e.nextElementSibling)
			if (e.matches(selector))
				return e;
	}
	return null;
}

function findChildren(element, selector) {
	const result = [];
	if (element) {
		for (let e = element.firstElementChild; e; e = e.nextElementSibling)
			if (e.matches(selector))
				result.push(e);
	}
	return result;
}

function onEvent(context, selector, events, fn) {
	context = context || document;
	function handler(e) {
		for (let el = e.target; el !== context && el !== null; el = el.parentElement) {
			if (el.matches(selector)) {
				fn.call(el, e, el);
				if (e.cancelBubble)
					break;
			}
		}
	}
	events.split(' ').forEach(event => context.addEventListener(event, handler));
}

const rgValidId = /[A-Za-z][A-Za-z0-9_:.-]*/;

/**
 * 
 * @param {HTMLElement?} context
 * @param {string} id
 * @returns { HTMLElement }
 */
function getById(context, id) {
	return rgValidId.test(id) ? (context || document).querySelector(`#${id}`) : null;
}


const uniqueId = (function () {
	let globalCount = 0;
	return function (element, prefix) {
		if (typeof element === "string") {
			prefix = element;
			element = null;
		}
		if (element && element.id)
			return element.id;
		const uniqueId = `${prefix || "uid"}-${new Date().valueOf()}-${++globalCount}`;
		if (element)
			element.id = uniqueId;
		return uniqueId;
	};
})();


/**
 * Sets HTML, runs scripts
 * @param {HTMLElement} target
 * @param {string?} html
 * @param {"before"|"after"|"append"|"prepend"|"content"|"replace"} where
 */
async function unsafeSetHTML(target, html, where) {
	if (html) {
		const fragment = document.createElement("template");
		fragment.innerHTML = html;
		// save scripts for later
		const scripts = [...fragment.content.querySelectorAll("script")];
		// copy content
		switch (where || "content") {
			case "before":
			case "after":
			case "prepend":
			case "append":
				target[where](fragment.content);
				break;
			case "content":
				target.replaceChildren(fragment.content);
				break;
			case "replace":
				target.replaceWith(fragment.content);
				break;
		}
		// process saved scripts
		for (const script of scripts) {
			// create new script element
			const scriptCopy = document.createElement("script");
			// copy attributes
			for (const attr of script.attributes) {
				// const attr = script.attributes[i];
				scriptCopy.setAttribute(attr.nodeName, attr.nodeValue);
			}
			if (script.src) {
				// if original script is not async, await for the result
				if (!script.async) {
					scriptCopy.async = true;
					await new Promise((resolve, reject) => {
						scriptCopy.onload = () => resolve();
						scriptCopy.onerror = () => reject(new Error("Failed to load script " + scriptCopy.src));
						script.replaceWith(scriptCopy);
					});
				} else {
					script.replaceWith(scriptCopy);
				}
			} else {
				scriptCopy.text = script.text;
				script.replaceWith(scriptCopy);
			}
		}
	}
}

/**
 * @param {HTMLElement} target
 * @param {string?} html
 * @param {"before"|"after"|"append"|"prepend"|"content"|"replace"} where
 */
function setHTML(target, html, where) {
	if (html) {
		const fragment = document.createElement("template");
		fragment.innerHTML = html;
		// copy content
		switch (where || "content") {
			case "before":
			case "after":
			case "prepend":
			case "append":
				target[where](fragment.content);
				break;
			case "content":
				target.replaceChildren(fragment.content);
				break;
			case "replace":
				target.replaceWith(fragment.content);
				break;
		}
	}
}

export { createElement, getById, findChild, findChildren, onEvent, uniqueId, setHTML, unsafeSetHTML };